import { useEffect, useRef, useState } from "react";
import { outreachFetch } from "../lib/business";
import { Link, useSearchParams } from "react-router-dom";
import { api, EmailLog } from "../lib/api";

const STATUSES = ["", "scheduled", "queued", "sending", "sent", "delivered", "bounced", "complained", "failed"] as const;
const PER_OPTIONS = [25, 50, 100];
const DEFAULT_PER = 50;

function fmt(ts: string | null): string {
  if (!ts) return "—";
  // Shown in the viewer's local timezone — label it so it's never mistaken for
  // the recipient's local time.
  return new Date(ts.replace(" ", "T") + "Z").toLocaleString(undefined, { timeZoneName: "short" });
}

export default function Logs() {
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState<EmailLog[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const q = params.get("q") || "";
  const status = params.get("status") || "";
  const campaignId = Number(params.get("campaign_id")) || 0;
  const page = Math.max(1, Number(params.get("page")) || 1);
  const per = PER_OPTIONS.includes(Number(params.get("per"))) ? Number(params.get("per")) : DEFAULT_PER;

  const [qInput, setQInput] = useState(q);
  const [campaigns, setCampaigns] = useState<{ id: number; name: string }[]>([]);
  const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Load the campaign/template list once for the filter dropdown.
  useEffect(() => {
    outreachFetch("/api/outreach/campaigns")
      .then((r) => r.json() as Promise<{ campaigns?: { id: number; name: string }[] }>)
      .then((d) => setCampaigns(d.campaigns || []))
      .catch(() => { /* dropdown just stays empty */ });
  }, []);

  // Patch a subset of params, resetting to page 1 unless paging.
  function patch(next: Record<string, string>, keepPage = false) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) { if (v) p.set(k, v); else p.delete(k); }
    if (!keepPage) p.delete("page");
    setParams(p, { replace: true });
  }

  useEffect(() => {
    setLoading(true); setErr(null);
    api.logs({ q, status, campaign_id: campaignId || undefined, page, per })
      .then((d) => { setRows(d.rows); setTotal(d.total); setPages(d.pages); })
      .catch((e) => setErr(String(e.message || e)))
      .finally(() => setLoading(false));
  }, [q, status, campaignId, page, per, refreshKey]);

  // Debounce the search box into the URL.
  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => { if (qInput !== q) patch({ q: qInput }); }, 350);
    return () => clearTimeout(debounce.current);
  }, [qInput]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">Email log</h1>
          <p className="mt-1 text-sm text-slate-500">
            Every outreach email — scheduled, queued, sent or failed — across all campaigns.
          </p>
        </div>
        <Link to="/outreach/campaigns" className="btn-secondary">Campaigns →</Link>
      </div>

      <div className="card flex flex-wrap items-center gap-2 p-3">
        <input
          className="!text-sm !py-2 !px-3 flex-1 min-w-[220px]"
          placeholder="Search recipient, contact, company or campaign…"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
        />
        <select className="!text-sm !py-2 !px-3" value={status} onChange={(e) => patch({ status: e.target.value })}>
          {STATUSES.map((s) => <option key={s} value={s}>{s ? s[0].toUpperCase() + s.slice(1) : "All statuses"}</option>)}
        </select>
        <select
          className="!text-sm !py-2 !px-3 max-w-[220px]"
          value={campaignId || ""}
          onChange={(e) => patch({ campaign_id: e.target.value })}
        >
          <option value="">All templates</option>
          {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          {/* keep a selected-but-unlisted campaign visible (e.g. deleted) */}
          {campaignId > 0 && !campaigns.some((c) => c.id === campaignId) && (
            <option value={campaignId}>Campaign #{campaignId}</option>
          )}
        </select>
        <div className="ml-auto flex items-center gap-2">
          <button
            className="btn-secondary !text-xs !py-2 !px-3"
            onClick={() => setRefreshKey((k) => k + 1)}
            disabled={loading}
            title="Refresh"
          >
            {loading ? "Refreshing…" : "↻ Refresh"}
          </button>
          <span className="text-xs text-slate-500">{total.toLocaleString()} email{total === 1 ? "" : "s"}</span>
          <select
            className="!text-sm !py-2 !px-2"
            value={per}
            onChange={(e) => patch({ per: e.target.value })}
            title="Emails per page"
          >
            {PER_OPTIONS.map((n) => <option key={n} value={n}>{n} / page</option>)}
          </select>
        </div>
      </div>

      {err && <div className="card p-4 text-rose-700">{err}</div>}

      <div className="card overflow-hidden">
        <table className="refined">
          <thead>
            <tr>
              <th className="w-28">Status</th>
              <th>Recipient</th>
              <th>Campaign</th>
              <th className="w-44">When</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.status}-${r.id}`}>
                <td>
                  <StatusPill v={r.status} />
                  {(r.opened_at || r.clicked_at) && (
                    <span className="ml-1 align-middle text-[11px]" title={
                      [r.opened_at ? `Opened ${fmt(r.opened_at)}` : "", r.clicked_at ? `Clicked ${fmt(r.clicked_at)}` : ""]
                        .filter(Boolean).join(" · ")
                    }>
                      {r.opened_at && "👁"}{r.clicked_at && "🔗"}
                    </span>
                  )}
                </td>
                <td>
                  <div className="font-mono text-xs text-slate-700">{r.to_email}</div>
                  {r.contact_name && (
                    <div className="text-xs text-slate-500">
                      {r.contact_name}{r.contact_company && <span className="text-slate-400"> · {r.contact_company}</span>}
                    </div>
                  )}
                </td>
                <td className="text-sm">
                  {r.campaign_id ? (
                    <Link to={`/outreach/campaigns/${r.campaign_id}`} className="text-slate-700 hover:underline">
                      {r.campaign_name || `#${r.campaign_id}`}
                    </Link>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="text-xs text-slate-500">
                  {r.status === "scheduled"
                    ? <span title="Scheduled send time">⏰ {fmt(r.scheduled_at)}</span>
                    : r.status === "sent"
                      ? fmt(r.sent_at)
                      : fmt(r.created_at)}
                </td>
                <td className="text-xs">
                  {r.error
                    ? <span className="text-rose-700">{r.error}</span>
                    : r.provider_id
                      ? <span className="font-mono text-slate-400">{r.provider_id}</span>
                      : <span className="text-slate-300">—</span>}
                </td>
              </tr>
            ))}
            {!loading && !rows.length && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500">No emails match.</td></tr>
            )}
            {loading && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button className="btn-secondary !text-xs !py-1.5 !px-3" disabled={page <= 1}
                  onClick={() => patch({ page: String(page - 1) }, true)}>← Prev</button>
          <span className="text-xs text-slate-500">Page {page} of {pages}</span>
          <button className="btn-secondary !text-xs !py-1.5 !px-3" disabled={page >= pages}
                  onClick={() => patch({ page: String(page + 1) }, true)}>Next →</button>
        </div>
      )}
    </div>
  );
}

function StatusPill({ v }: { v: string }) {
  const cls = {
    scheduled:  "pill pill-blue",
    queued:     "pill pill-slate",
    sending:    "pill pill-amber",
    sent:       "pill pill-slate",   // accepted by Resend, no delivery event yet
    delivered:  "pill pill-green",
    bounced:    "pill pill-rose",
    complained: "pill pill-rose",
    failed:     "pill pill-rose",
  }[v] || "pill pill-slate";
  return <span className={cls}>{v}</span>;
}
